/**
 * Variacao contra o periodo anterior: glifo + texto, nunca so cor.
 *
 * Decisoes nao obvias:
 *  1. O glifo (triangulo) e decorativo e o texto diz "alta de 12,3%": quem
 *     nao distingue verde de vermelho, e o leitor de tela, recebem a mesma
 *     informacao.
 *  2. Medida em bps (taxa) nao mostra variacao relativa: "+15% na taxa" e
 *     ambiguo. Mostra "de 3,1% para 4,2%".
 *  3. `sentido` decide a cor. Custo e "neutro" por padrao: sobe junto com o
 *     volume trabalhado; o custo por recuperacao e que diz se foi bom.
 *  4. `compacto` e a forma de coluna de tabela: so glifo + relativo, com o
 *     texto completo no `title`.
 */

import { formatBRLFromCents, formatCount, formatPercentFromBps } from '../../../lib/format';
import { variar } from '../comparar';

export type TipoDeMedida = 'dinheiro' | 'contagem' | 'bps';
export type SentidoDoDelta = 'maior_melhor' | 'menor_melhor' | 'neutro';

function formatarAbsoluto(valor: number, tipo: TipoDeMedida): string {
  const sinal = valor > 0 ? '+' : valor < 0 ? '−' : '';
  const abs = Math.abs(valor);
  return `${sinal}${tipo === 'dinheiro' ? formatBRLFromCents(abs) : formatCount(abs)}`;
}

function corDo(alta: boolean, sentido: SentidoDoDelta): string {
  if (sentido === 'neutro') {
    return 'text-ink-muted';
  }
  const bom = sentido === 'maior_melhor' ? alta : !alta;
  return bom ? 'text-status-good' : 'text-status-critical';
}

export function Delta({
  atual,
  anterior,
  tipo,
  sentido = 'maior_melhor',
  compacto = false,
  className = '',
}: {
  readonly atual: number;
  /** `null` quando a janela nao tem periodo anterior. */
  readonly anterior: number | null;
  readonly tipo: TipoDeMedida;
  readonly sentido?: SentidoDoDelta;
  readonly compacto?: boolean;
  readonly className?: string;
}): JSX.Element {
  const variacao = variar(atual, anterior);
  const base = `inline-flex flex-wrap items-center gap-1 text-xs tabular-nums ${className}`;
  const titulo = 'Comparado com o período anterior de mesmo tamanho';

  if (variacao.direcao === 'sem_base') {
    return (
      <span className={`${base} text-ink-subtle`} title={titulo}>
        {compacto ? '—' : 'sem período anterior para comparar'}
        {compacto ? <span className="sr-only">sem base anterior</span> : null}
      </span>
    );
  }

  if (variacao.direcao === 'igual') {
    return (
      <span className={`${base} text-ink-subtle`} title={titulo}>
        <span aria-hidden="true">=</span> {compacto ? 'igual' : 'igual ao período anterior'}
      </span>
    );
  }

  const alta = variacao.direcao === 'alta';
  const cor = corDo(alta, sentido);
  const glifo = alta ? '▲' : '▼';
  const palavra = alta ? 'alta' : 'queda';

  if (tipo === 'bps') {
    const texto = `de ${formatPercentFromBps(anterior ?? 0)} para ${formatPercentFromBps(atual)}`;
    return (
      <span className={`${base} ${cor}`} title={`${titulo}: ${texto}`}>
        <span aria-hidden="true">{glifo}</span>
        <span className="sr-only">{palavra}:</span>
        <span>{compacto ? formatPercentFromBps(atual) : texto}</span>
      </span>
    );
  }

  const absoluto = formatarAbsoluto(variacao.absoluta, tipo);
  const relativo = variacao.relativaBps === null ? null : formatPercentFromBps(Math.abs(variacao.relativaBps));

  if (compacto) {
    return (
      <span className={`${base} ${cor}`} title={`${titulo}: ${palavra} de ${relativo ?? absoluto}`}>
        <span aria-hidden="true">{glifo}</span>
        <span className="sr-only">{palavra} de</span>
        <span>{relativo ?? absoluto}</span>
      </span>
    );
  }

  return (
    <span className={`${base} ${cor}`} title={titulo}>
      <span aria-hidden="true">{glifo}</span>
      <span className="sr-only">{palavra} de</span>
      {relativo === null ? (
        <span>
          {absoluto} <span className="text-ink-subtle">(antes: 0)</span>
        </span>
      ) : (
        <span>
          {relativo} <span className="text-ink-subtle">({absoluto} vs. anterior)</span>
        </span>
      )}
    </span>
  );
}
